import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useMemo } from 'react'
import * as yup from 'yup'
import { useTranslations } from '@/lib/next-shim'

// A minimal translator type: pass-through (key === message) when no `t` is supplied,
// so the schema stays usable for type inference and unit tests without React context.
type Translate = (key: string) => string
const identity: Translate = (key) => key

// CubeSanté (France) sign-up is intentionally minimal: identity (birthday/gender),
// social-security number and establishment are deferred to the post-signup
// "complete profile" step (gated before a video consultation), matching the market.
export function buildCubeSanteRegisterSchema(t: Translate = identity) {
  return yup.object({
    email: yup.string().email(t('validation.email.invalid')).required(t('validation.email.required')),
    name: yup.string().required(t('validation.firstName.required')),
    surname: yup.string().required(t('validation.lastName.required')),
    phone: yup.string(),
    terms: yup.boolean().oneOf([true], t('validation.terms.required')).required(t('validation.terms.required')),
    password: yup.string().required(t('validation.password.required')).min(8, t('validation.password.min')),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('validation.password.mustMatch'))
      .required(t('validation.confirmPassword.required')),
  })
}

// Schema with key-as-message defaults — drives the form-data type and unit tests.
export const cubeSanteRegisterSchema = buildCubeSanteRegisterSchema()

export type CubeSanteRegisterFormData = yup.InferType<typeof cubeSanteRegisterSchema>

export function useCubeSanteRegisterForm() {
  const t = useTranslations()
  const schema = useMemo(() => buildCubeSanteRegisterSchema(t), [t])
  return useForm<CubeSanteRegisterFormData>({
    defaultValues: {
      email: '',
      name: '',
      surname: '',
      phone: '',
      terms: false,
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  })
}
